import {
    QueryClient,
    useMutation,
    UseMutationResult,
    useQuery,
    useQueryClient,
    UseQueryResult
} from '@tanstack/react-query';

import { TransactionModel } from 'src/types/transaction.model';
import { LocalDate } from '@js-joda/core';
import TransactionService from 'src/services/transaction.service';
import { TransactionType } from 'src/types/transaction-type.enum';
import { PaginatedTransactionsModel } from 'src/types/paginated-transactions.model';
import { invalidateBudgets } from 'src/hooks/queries/budgets';
import { TransactionField } from 'src/bw/project-jaeger/data/TransactionField.enum';
import { invalidateTransactionsSpending } from 'src/hooks/queries/spending';

export const transactionsQueryKey: string = 'transactions';
export const transactionQueryKey: string = 'transaction';
const reviewableTransactionsQueryKey: string = 'review';

export const useFilteredTransactions = ({
    startDate,
    endDate,
    search,
    account,
    type,
    group,
    income,
    field = TransactionField.DATE,
    isAscending = false,
    page = 1,
    showHidden = false
}: {
    startDate?: LocalDate;
    endDate?: LocalDate;
    search?: string;
    account?: string;
    type?: TransactionType;
    group?: string;
    income?: string;
    field?: string;
    isAscending?: boolean;
    page?: number;
    showHidden?: boolean;
}): UseQueryResult<PaginatedTransactionsModel> => {
    return useQuery<PaginatedTransactionsModel>({
        queryKey: [
            transactionsQueryKey,
            startDate,
            endDate,
            search,
            account,
            type,
            group,
            income,
            field,
            isAscending,
            page,
            showHidden
        ],
        queryFn: async () =>
            await TransactionService.getFilteredTransactions(
                startDate,
                endDate,
                search,
                account,
                type,
                group,
                income,
                field,
                isAscending,
                page,
                showHidden
            ),
        keepPreviousData: true
    });
};

export const useReviewableTransactions = ({
    search,
    field = TransactionField.DATE,
    isAscending = false,
    enabled = true,
    page = 1
}: {
    search?: string;
    field?: string;
    isAscending?: boolean;
    enabled?: boolean;
    page?: number;
}): UseQueryResult<PaginatedTransactionsModel> => {
    return useQuery<PaginatedTransactionsModel>({
        queryKey: [transactionsQueryKey, reviewableTransactionsQueryKey, field, isAscending, search, page],
        queryFn: async () =>
            await TransactionService.getFilteredTransactionsForReview(field, isAscending, search, page),
        keepPreviousData: true,
        enabled
    });
};

export const useTransaction = (id: number): UseQueryResult<TransactionModel> => {
    return useQuery<TransactionModel>({
        queryKey: [transactionQueryKey, id],
        queryFn: async () => await TransactionService.getTransaction(id)
    });
};

export const useUpdateTransaction = (id?: number): UseMutationResult<TransactionModel, unknown, TransactionModel> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (transaction: TransactionModel) => await TransactionService.updateTransaction(transaction),
        mutationKey: [transactionQueryKey, id],
        onSuccess: (_, transaction): void => {
            void invalidateTransactionsSpending(queryClient, transaction.id);
            void invalidateBudgets(queryClient);
        }
    });
};

export const useUpdateTransactionsGroup = (): UseMutationResult<
    any,
    unknown,
    { transactions: TransactionModel[]; groupUid: string }
> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ transactions, groupUid }) =>
            await TransactionService.updateTransactionsGroup(transactions, groupUid),
        mutationKey: [transactionsQueryKey, 'group'],
        onSuccess: (): void => {
            void invalidateTransactionsSpending(queryClient);
            void invalidateBudgets(queryClient);
        }
    });
};

export const useUpdateTransactions = (): UseMutationResult<any, unknown, TransactionModel[]> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (transactions: TransactionModel[]) =>
            await TransactionService.updateTransactions(transactions),
        onSuccess: (): void => {
            void invalidateTransactionsSpending(queryClient);
            void invalidateBudgets(queryClient);
        }
    });
};

export const useReviewTransaction = (): UseMutationResult<void, unknown, number> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (transactionId: number) => {
            await TransactionService.markTransactionReviewed(transactionId);
        },
        onSuccess: (): void => {
            void invalidateReviewTransactions(queryClient);
        }
    });
};

export const useReviewTransactions = (): UseMutationResult<void, unknown, number[]> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (transactionIds: number[]) => {
            await TransactionService.markTransactionsReviewed(transactionIds);
        },
        onSuccess: (): void => {
            void invalidateReviewTransactions(queryClient);
        }
    });
};

export const invalidateTransactions = async (queryClient: QueryClient, transactionId?: number): Promise<void> => {
    await queryClient.invalidateQueries([transactionsQueryKey]);
    await queryClient.invalidateQueries(transactionId ? [transactionQueryKey, transactionId] : [transactionQueryKey]);
};

export const invalidateReviewTransactions = async (queryClient: QueryClient): Promise<void> => {
    await queryClient.invalidateQueries([transactionsQueryKey, reviewableTransactionsQueryKey]);
};
